import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, type Video } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const video = useMemo(() => {
    return videos.find((v) => v.id === Number(id));
  }, [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id && v.category === video.category)
      .slice(0, 4);
  }, [video]);

  const categoryName = useMemo(() => {
    if (!video) return '';
    return categories.find((c) => c.slug === video.category)?.name || '';
  }, [video]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex flex-col items-center justify-center py-32">
            <i className="ri-error-warning-line text-6xl text-foreground-400 mb-4"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">该视频可能已被移除或链接无效</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formattedViews = video.views >= 10000
    ? `${(video.views / 10000).toFixed(1)}万次播放`
    : `${video.views}次播放`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <article className="pb-14" itemScope itemType="https://schema.org/VideoObject">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-2" itemProp="name">
              {video.title}
            </h1>
            <div className="flex items-center gap-4 mb-5 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formattedViews}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <span itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                  <meta itemProp="ratingValue" content={String(video.rating)} />
                  <meta itemProp="ratingCount" content={String(video.views)} />
                  {video.rating}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {video.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {video.duration}
              </span>
            </div>

            <meta itemProp="thumbnailUrl" content={video.thumbnail} />
            <meta itemProp="uploadDate" content={video.date} />

            <div className="relative aspect-video rounded-lg overflow-hidden bg-black mb-6">
              <img
                src={video.thumbnail}
                alt={video.title}
                title={`${video.title} - 高清视频在线播放`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <button className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-colors cursor-pointer group">
                  <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1 group-hover:scale-110 transition-transform"></i>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-3-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">91高清视频</p>
                    <p className="text-xs text-foreground-500">{formattedViews}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h2 className="text-base font-semibold text-foreground-950 mb-3 font-heading">视频简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed" itemProp="description">
                  {video.description}
                </p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground-950 mb-4 font-heading">
                  相关推荐
                </h3>
                <div className="space-y-4" data-product-shop="">
                  {relatedVideos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/video/${v.id}`}
                      className="flex gap-3 cursor-pointer group"
                      aria-label={`观看 ${v.title}`}
                    >
                      <div className="relative w-40 h-24 rounded-md overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <span className="absolute bottom-1 right-1 bg-black/80 text-background-50 text-[10px] px-1.5 py-0.5 rounded">
                          {v.duration}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {v.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-foreground-500">
                          <span>{v.views >= 10000 ? `${(v.views / 10000).toFixed(1)}万` : v.views}</span>
                          <span>·</span>
                          <span>{v.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.example.com/category/${video.category}` },
              { '@type': 'ListItem', position: 3, name: video.title },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: video.title,
            description: video.description,
            datePublished: video.date,
            author: {
              '@type': 'Organization',
              name: '91高清视频',
            },
            publisher: {
              '@type': 'Organization',
              name: '91高清视频',
            },
          }),
        }}
      />
    </>
  );
}